import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { DataItem } from 'scrivito'

import { buildDocumentDrawerMenuItems } from './buildDocumentDrawerMenuItems'
import { DocumentDrawerForm } from './DocumentDrawerForm'
import { DocumentDrawerHeader } from './Header'
import { Button } from '@/Atomic/Molecules/Button'
import { Drawer, DrawerBody, DrawerFooter } from '@/Atomic/Templates/Drawer'
import { DocumentBodyBinary } from '@/Components/Document/types'
import { useDataItemFormHandler } from '@/utils/CRUD/hooks/useDataItemFormHandler'
import { dataBinaryToUrl } from '@/utils/dataBinaryToUrl'

import './DocumentDrawer.scss'

interface DocumentDrawerProps {
  document: DataItem
  isOpen: boolean
  close: () => void
  startInEditMode?: boolean
  openDrawerId?: (id?: string) => void
  nextId?: string
  previousId?: string
}

export const DocumentDrawer: React.FC<DocumentDrawerProps> = ({
  document,
  isOpen,
  close,
  startInEditMode,
  openDrawerId,
  nextId,
  previousId,
}) => {
  const { t } = useTranslation()
  const [downloadUrl, setDownloadUrl] = useState('')

  const {
    methods,
    onFormSubmit,
    handleSave,
    isEditing,
    abortEditing,
    setIsEditing,
  } = useDataItemFormHandler(document)

  useEffect(() => {
    if (isOpen && startInEditMode) {
      setIsEditing(true)
    }
  }, [isOpen, startInEditMode, setIsEditing])

  const { body, downloadName } = useMemo(() => {
    const body = document.get('body') as DocumentBodyBinary | undefined
    const displayTitle =
      (document.get('displayTitle') as string) || body?.filename || ''

    return { body, downloadName: displayTitle }
  }, [document])

  useEffect(() => {
    if (!body) return

    let cancelled = false
    const animationFrame = window.requestAnimationFrame(() => {
      setDownloadUrl('')
    })

    const fetch = async () => {
      const res = await dataBinaryToUrl(body)
      if (!cancelled) {
        setDownloadUrl(res?.url || '')
      }
    }

    fetch()

    return () => {
      cancelled = true
      window.cancelAnimationFrame(animationFrame)
    }
  }, [body])

  const handleClose = () => {
    if (isEditing) {
      abortEditing()
    }
    close()
  }

  const menuItems = useMemo(
    () =>
      buildDocumentDrawerMenuItems({
        t,
        document,
        downloadUrl,
        downloadName,
      }),
    [document, downloadName, downloadUrl, t],
  )

  const handleNavigate = (targetId?: string) => {
    if (!openDrawerId || !targetId) return

    if (isEditing) {
      abortEditing()
    }

    openDrawerId(targetId)
  }

  return (
    <Drawer isOpen={isOpen} close={handleClose} direction="right">
      <DocumentDrawerHeader
        handleClose={handleClose}
        handleNavigate={handleNavigate}
        isEditing={isEditing}
        menuItems={menuItems}
        openDrawerId={openDrawerId}
        setIsEditing={setIsEditing}
        nextId={nextId}
        previousId={previousId}
      />
      <DrawerBody>
        <DocumentDrawerForm
          dataItem={document}
          methods={methods}
          onFormSubmit={onFormSubmit}
          isEditing={isEditing}
        />
      </DrawerBody>
      {isEditing && (
        <DrawerFooter align="right">
          <Button variant="outlined" onClick={abortEditing}>
            {t('common.cancel')}
          </Button>
          <Button icon="disk" onClick={handleSave}>
            {t('common.save')}
          </Button>
        </DrawerFooter>
      )}
    </Drawer>
  )
}
